import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SiteContentContext = createContext({ contentByPage: {}, get: () => ({}), refresh: () => {} });

export function SiteContentProvider({ children }) {
  const [contentByPage, setContentByPage] = useState({});

  const fetchPage = useCallback(async (page) => {
    try {
      const r = await api.get(`/site-content/${page}`);
      setContentByPage((prev) => ({ ...prev, [page]: r.data.content }));
    } catch (e) {
      // Non-fatal: falls back to hardcoded defaults in the consuming component.
      // Log so real network/CORS failures don't silently hide.
      console.warn(`[SiteContent] failed to fetch page '${page}':`, e?.message || e);
    }
  }, []);

  // Method to grab content for a page — fetch if not cached, returns whatever we have right now
  const get = useCallback(
    (page) => {
      if (!(page in contentByPage)) {
        // kick off background fetch but return {} synchronously
        fetchPage(page);
        return {};
      }
      return contentByPage[page] || {};
    },
    [contentByPage, fetchPage]
  );

  const refresh = useCallback((page) => fetchPage(page), [fetchPage]);

  return (
    <SiteContentContext.Provider value={{ contentByPage, get, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

/**
 * Hook: returns the admin-editable content for a page, merged with hardcoded defaults you pass in.
 * Usage:
 *   const c = useSiteContent('home', { hero_title: 'fallback...' });
 *   <h1>{c.hero_title}</h1>
 */
export function useSiteContent(page, defaults = {}) {
  const ctx = useContext(SiteContentContext);
  const [content, setContent] = useState(ctx.contentByPage[page] || {});

  useEffect(() => {
    setContent(ctx.contentByPage[page] || {});
    if (!(page in ctx.contentByPage)) {
      ctx.refresh(page);
    }
  }, [page, ctx.contentByPage, ctx]);

  return { ...defaults, ...content };
}
